import { Head, Link, router, useForm, usePage } from "@inertiajs/react"
import { createConsumer } from "@rails/actioncable"
import { useEffect, useEffectEvent, useState } from "react"

import { deleteJson, postJson } from "@/api/client"
import HighlightedText from "@/components/highlighted-text"
import MentionSummary from "@/components/mention-summary"
import PlatformLimitIndicator from "@/components/platform-limit-indicator"
import CopyImageButton from "@/components/stories/copy-image-button"
import CopyTextButton from "@/components/stories/copy-text-button"
import ExportActions from "@/components/stories/export-actions"
import MarkdownPreview from "@/components/stories/markdown-preview"
import MediaExportButtons from "@/components/stories/media-export-buttons"
import QuotedTakeReference from "@/components/stories/quoted-take-reference"
import SyndicationCards from "@/components/stories/syndication-cards"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTimeZone } from "@/hooks/use-time-zone"
import { useVisiblePlatforms } from "@/hooks/use-visible-platforms"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/format"
import { applyMentionFallbacks } from "@/lib/mention-fallback"
import {
  duplicateStoryPath,
  editStoryPath,
  publicStoryPath,
  publishReadyStorySyndicationsPath,
  storiesPath,
  storyPath,
  storySchedulePath,
  storySyndicationsPath,
} from "@/routes"
import type {
  BreadcrumbItem,
  CodeTake,
  MentionHandleGroup,
  QuoteTake,
  Story,
  Syndication,
  Take,
  TakeMediaFile,
} from "@/types"

function isVideoContentType(contentType?: string | null) {
  return contentType?.startsWith("video/") ?? false
}

function isCodeTake(take: Take | CodeTake): take is CodeTake {
  return take.type === "CodeTake"
}

function isQuoteTake(take: Take | CodeTake | QuoteTake): take is QuoteTake {
  return take.type === "QuoteTake"
}

export default function StoriesShow({
  story,
  mention_handle_groups,
}: {
  story: Story
  mention_handle_groups: MentionHandleGroup[]
}) {
  usePage()
  const timeZone = useTimeZone()
  const { delete: destroy, processing } = useForm()
  const [syndications, setSyndications] = useState<Syndication[]>(
    story.syndications,
  )
  const [subscribed, setSubscribed] = useState(false)
  const [publishing, setPublishing] = useState<Set<number>>(new Set())
  const [bulkPublishing, setBulkPublishing] = useState(false)
  const [bulkError, setBulkError] = useState<string | null>(null)
  const [canceling, setCanceling] = useState(false)
  const [copied, setCopied] = useState(false)
  const [retryErrors, setRetryErrors] = useState<Record<number, string>>({})
  const handleReceived = useEffectEvent((data: Syndication) => {
    setSyndications((prev) =>
      prev.some((s) => s.id === data.id)
        ? prev.map((s) => (s.id === data.id ? data : s))
        : [...prev, data],
    )
    setPublishing((prev) => {
      const next = new Set(prev)
      next.delete(data.id)
      return next
    })
  })

  useEffect(() => {
    const consumer = createConsumer()
    const subscription = consumer.subscriptions.create(
      { channel: "StoryChannel", story_id: story.id },
      {
        connected() {
          setSubscribed(true)
        },
        disconnected() {
          setSubscribed(false)
        },
        received(data: Syndication) {
          handleReceived(data)
        },
      },
    )
    return () => {
      setSubscribed(false)
      subscription.unsubscribe()
      consumer.disconnect()
    }
  }, [story.id])

  function handleDelete() {
    if (confirm("Are you sure you want to delete this story?")) {
      destroy(storyPath(story.id))
    }
  }

  function handleDuplicate() {
    router.post(duplicateStoryPath(story.id))
  }

  async function handleCopyLink() {
    const url = `${window.location.origin}${publicStoryPath(story.public_token)}`
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  async function handleRetry(syndication: Syndication) {
    const syndicationId = syndication.id
    setPublishing((prev) => new Set(prev).add(syndicationId))
    setRetryErrors((prev) => {
      const next = { ...prev }
      delete next[syndicationId]
      return next
    })
    const result = await postJson<Syndication>(
      storySyndicationsPath(story.id),
      { syndication: { id: syndicationId } },
    )

    if (!result.ok) {
      setRetryErrors((prev) => ({
        ...prev,
        [syndicationId]: result.errors[0] ?? "Unable to publish this story.",
      }))
      setPublishing((prev) => {
        const next = new Set(prev)
        next.delete(syndicationId)
        return next
      })
      return
    }

    upsertSyndication(result.data)
  }

  async function handlePublishAll() {
    setBulkPublishing(true)
    setBulkError(null)
    const readyIds = syndications
      .filter((s) => s.status === "ready")
      .map((s) => s.id)
    setPublishing((prev) => {
      const next = new Set(prev)
      readyIds.forEach((id) => next.add(id))
      return next
    })

    const result = await postJson<{ syndications: Syndication[] }>(
      publishReadyStorySyndicationsPath(story.id),
    )

    if (!result.ok) {
      setBulkError(result.errors[0] ?? "Unable to publish this story.")
      setPublishing((prev) => {
        const next = new Set(prev)
        readyIds.forEach((id) => next.delete(id))
        return next
      })
      setBulkPublishing(false)
      return
    }

    result.data.syndications.forEach(upsertSyndication)
    setBulkPublishing(false)
  }

  async function handleCancelSchedule() {
    setCanceling(true)
    setBulkError(null)
    const result = await deleteJson(storySchedulePath(story.id))

    if (!result.ok) {
      setBulkError(result.errors[0] ?? "Unable to cancel the schedule.")
      setCanceling(false)
      return
    }

    router.reload({ only: ["story"] })
    setCanceling(false)
  }

  function upsertSyndication(nextSyndication: Syndication) {
    setSyndications((prev) =>
      prev.some((syndication) => syndication.id === nextSyndication.id)
        ? prev.map((syndication) =>
            syndication.id === nextSyndication.id
              ? nextSyndication
              : syndication,
          )
        : [...prev, nextSyndication],
    )
    setPublishing((prev) => {
      const next = new Set(prev)
      next.delete(nextSyndication.id)
      return next
    })
  }

  const visiblePlatforms = useVisiblePlatforms(
    syndications.map((s) => s.platform),
  )
  const readyCount = syndications.filter((s) => s.status === "ready").length
  const hasReady = readyCount > 0
  const isScheduled = story.scheduled
  const publishDisabled =
    !hasReady ||
    isScheduled ||
    !subscribed ||
    !story.publishable ||
    bulkPublishing
  const publishDisabledReason = bulkPublishing
    ? null
    : isScheduled
      ? "Scheduling is already set; edit the story to change the time."
      : !story.publishable
        ? (story.publish_errors[0] ?? "This story isn't ready to publish.")
        : !hasReady
          ? "Nothing left to publish."
          : !subscribed
            ? "Reconnecting — try again in a moment."
            : null
  const storyStatusLabel =
    syndications.length === 0
      ? "Draft"
      : isScheduled
        ? "Scheduled"
        : syndications.every((s) => s.status === "ready")
          ? "Ready to publish"
          : "Published"

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Newsroom", href: storiesPath() },
    { title: "Story", href: storyPath(story.id) },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Story" />

      <div className="flex flex-col gap-6 p-6">
        <div className="flex flex-col items-start gap-4 border-b pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-muted-foreground text-xs font-medium tracking-widest uppercase">
              {storyStatusLabel}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {syndications.length > 0 &&
              (publishDisabled && publishDisabledReason ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0}>
                      <Button
                        size="sm"
                        disabled
                        className="pointer-events-none"
                      >
                        {bulkPublishing ? "Publishing..." : "Publish now"}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{publishDisabledReason}</TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  size="sm"
                  disabled={publishDisabled}
                  onClick={() => void handlePublishAll()}
                >
                  {bulkPublishing ? "Publishing..." : "Publish now"}
                </Button>
              ))}
            {syndications.some((s) => s.status === "success") && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleCopyLink()}
              >
                {copied ? "Copied!" : "Copy Shareable Link"}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDuplicate}
              disabled={processing}
            >
              Duplicate
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link href={editStoryPath(story.id)}>Edit</Link>
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={processing}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-start">
          <div className="flex flex-col gap-8">
            {story.long_form ? (
              <MarkdownPreview source={story.takes[0]?.body ?? ""} />
            ) : (
              story.takes.map((take, index) => {
                const visibleMediaFiles = take.media_files.filter(
                  (mediaFile: TakeMediaFile) => mediaFile.url,
                )
                const imageCount = visibleMediaFiles.filter(
                  (mediaFile: TakeMediaFile) =>
                    !isVideoContentType(mediaFile.content_type),
                ).length
                const copyBody = applyMentionFallbacks(
                  take.body ?? "",
                  mention_handle_groups,
                )

                return (
                  <section
                    key={take.id}
                    className="flex flex-col gap-4 border-b pb-8 last:border-b-0 last:pb-0"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-muted-foreground text-xs font-medium tracking-widest uppercase">
                        Take {index + 1}
                      </p>
                      <PlatformLimitIndicator
                        length={(take.body ?? "").length}
                        platforms={visiblePlatforms}
                      />
                    </div>

                    <div className="relative">
                      <HighlightedText
                        text={take.body ?? ""}
                        className="font-serif leading-relaxed whitespace-pre-wrap"
                        mentionHandleGroups={mention_handle_groups}
                      />
                      <CopyTextButton body={copyBody} />
                    </div>
                    <MentionSummary
                      body={take.body ?? ""}
                      mentionHandleGroups={mention_handle_groups}
                    />

                    {isQuoteTake(take) && take.quoted_take && (
                      <QuotedTakeReference quotedTake={take.quoted_take} />
                    )}

                    {isCodeTake(take) && take.code_image_url && (
                      <figure className="flex flex-col gap-2 rounded-xl border p-3">
                        <p className="text-muted-foreground text-xs font-medium tracking-widest uppercase">
                          Saved image
                        </p>
                        <Dialog>
                          <DialogTrigger asChild>
                            <img
                              src={take.code_image_url}
                              alt="Generated code take image"
                              className="cursor-pointer rounded-md border"
                            />
                          </DialogTrigger>
                          <DialogContent className="p-2 sm:max-w-4xl">
                            <DialogTitle className="sr-only">
                              Image preview
                            </DialogTitle>
                            <img
                              src={take.code_image_url}
                              alt="Generated code take image"
                              className="max-h-[80vh] w-full rounded-md object-contain"
                            />
                          </DialogContent>
                        </Dialog>
                      </figure>
                    )}

                    {!isCodeTake(take) && visibleMediaFiles.length > 0 && (
                      <div className="flex items-center gap-3">
                        <p className="text-muted-foreground text-xs font-medium tracking-widest uppercase">
                          Media files
                        </p>
                        <PlatformLimitIndicator
                          length={imageCount}
                          metric="images"
                          platforms={visiblePlatforms}
                        />
                      </div>
                    )}

                    {!isCodeTake(take) && visibleMediaFiles.length > 0 && (
                      <div className="flex flex-wrap gap-3">
                        {visibleMediaFiles.map((mediaFile: TakeMediaFile) => (
                          <figure
                            key={mediaFile.id}
                            className="flex max-w-fit flex-col gap-2"
                          >
                            {isVideoContentType(mediaFile.content_type) ? (
                              <video
                                src={mediaFile.url!}
                                className="max-h-96 rounded-md object-cover"
                                controls
                                playsInline
                                preload="metadata"
                              />
                            ) : (
                              <Dialog>
                                <DialogTrigger asChild>
                                  <div className="relative w-fit cursor-pointer">
                                    <img
                                      src={mediaFile.url!}
                                      alt={mediaFile.alt_text ?? ""}
                                      className="max-h-96 rounded-md object-cover"
                                    />
                                    {mediaFile.alt_text && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="bg-background/80 text-foreground absolute bottom-2 left-2 cursor-default rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide select-none">
                                            ALT
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent
                                          side="top"
                                          className="max-w-72"
                                        >
                                          {mediaFile.alt_text}
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                    <CopyImageButton
                                      url={mediaFile.url!}
                                      contentType={mediaFile.content_type}
                                    />
                                  </div>
                                </DialogTrigger>
                                <DialogContent className="p-2 sm:max-w-4xl">
                                  <DialogTitle className="sr-only">
                                    Image preview
                                  </DialogTitle>
                                  <img
                                    src={mediaFile.url!}
                                    alt={mediaFile.alt_text ?? ""}
                                    className="max-h-[80vh] w-full rounded-md object-contain"
                                  />
                                  {mediaFile.alt_text && (
                                    <p className="text-muted-foreground px-2 pb-2 text-sm">
                                      {mediaFile.alt_text}
                                    </p>
                                  )}
                                </DialogContent>
                              </Dialog>
                            )}
                            {!isVideoContentType(mediaFile.content_type) && (
                              <MediaExportButtons
                                url={mediaFile.url!}
                                contentType={mediaFile.content_type}
                              />
                            )}
                          </figure>
                        ))}
                      </div>
                    )}

                    {!isCodeTake(take) && (
                      <ExportActions
                        body={copyBody}
                        mediaFiles={visibleMediaFiles}
                      />
                    )}
                  </section>
                )
              })
            )}
          </div>

          <aside className="flex flex-col gap-4 border-t lg:border-t-0">
            {isScheduled && story.scheduled_at && (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-blue-200 bg-blue-50/80 p-3 dark:border-blue-900/40 dark:bg-blue-950/20">
                <p className="text-sm">
                  Scheduled for {formatDate(story.scheduled_at, true, timeZone)}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={canceling}
                  onClick={() => void handleCancelSchedule()}
                >
                  Cancel schedule
                </Button>
              </div>
            )}

            {bulkError && (
              <p role="alert" className="text-sm text-red-600">
                {bulkError}
              </p>
            )}

            <SyndicationCards
              storyId={story.id}
              publishAt={isScheduled ? story.scheduled_at : null}
              publishable={story.publishable}
              publishErrors={story.publish_errors}
              syndications={syndications}
              publishing={publishing}
              subscribed={subscribed}
              retryErrors={retryErrors}
              onRetry={handleRetry}
            />
          </aside>
        </div>
      </div>
    </AppLayout>
  )
}
